import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Drawer,
  FormInput,
  FormSelect,
} from "@stonelog/stonelog-design-system";
import {
  brazillianStates,
  toCapitalizeFirstLetterOfEachWord,
} from "../../../../../../utils/common";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { schema } from "./schema";
import {
  capitalizeFirstWord,
} from "../../../../../../utils/formatter";
import {
  clearZipCodeAddressInformation,
  getZipCodeAddressInformation,
  zipCodeAddressInformationSelector,
} from "../../../../../../features/senninha/zipCodeAddressInformationSlice";
import {
  postProviderInformation,
  providerInformationsSelector,
  setResetPostProviderInformationStatus,
} from "../../../../../../features/senninha/GestaoEquipe/providerInformationsSlice";
import { hubsSelector } from "../../../../../../features/senninha/hubsSlice";
import { employeesInfosLogisticSelector } from "../../../../../../features/senninha/employeesInfosLogistic";
import { useDispatch, useSelector } from "react-redux";
import warning from "@assets/icons/Warning.svg";

import * as S from "./styles";


const InfosProviderEditing = ({
  openProviderEditDrawer,
  setOpenProviderEditDrawer,
}) => {
  const [openCloseModal, setOpenCloseModal] = useState(false);

  const dispatch = useDispatch();

  const {
    zipCodeAddressInformationData,
    zipCodeAddressInformationStatus,
  } = useSelector(zipCodeAddressInformationSelector);

  const {
    getProviderInformationData,
    postProviderInformationStatus,
  } = useSelector(providerInformationsSelector);

  const { selectedHubInfos } = useSelector(hubsSelector);

  const { employeesInfosLogisticData } = useSelector(
    employeesInfosLogisticSelector
  );

  const {
    control,
    handleSubmit,
    reset,
    formState,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (getProviderInformationData) {
      reset({
        razaoSocial:
          toCapitalizeFirstLetterOfEachWord(getProviderInformationData.razao_social) || "",
        nomeFantasia:
          capitalizeFirstWord(getProviderInformationData.nome_fantasia) || "",
        cpf: getProviderInformationData.documento || "",
        email: getProviderInformationData.email || "",
        pessoaResponsavel: toCapitalizeFirstLetterOfEachWord(getProviderInformationData?.pessoa_responsavel) || "",
        contato: getProviderInformationData.contato || "",
        cep: getProviderInformationData.cep || "",
        endereco: getProviderInformationData.endereco || "",
        numeroEndereco: getProviderInformationData.numero_endereco || "",
        complemento: toCapitalizeFirstLetterOfEachWord(getProviderInformationData?.complemento) || "",
        bairro: toCapitalizeFirstLetterOfEachWord(getProviderInformationData?.bairro) || "",
        uf: getProviderInformationData.uf || "",
        cidade: toCapitalizeFirstLetterOfEachWord(getProviderInformationData?.cidade) || "",
        latitude: getProviderInformationData.latitude || "",
        longitude: getProviderInformationData.longitude || "",
      });
    }
  }, [getProviderInformationData, reset]);

  const onChangeZipCode = (e) => {
    let cep = e.target.value;
    setValue("cep", cep);
    if (cep[5] === "-" && cep.length === 9) {
      dispatch(getZipCodeAddressInformation(cep.replace("-", "")));
    }
    if (cep.length === 8 && cep[5] !== "-") {
      dispatch(getZipCodeAddressInformation(cep));
    }
  };

  useEffect(() => {
    if (zipCodeAddressInformationStatus === "fulfilled") {
      if (zipCodeAddressInformationData && Object.keys(zipCodeAddressInformationData).length > 0) {
        const { logradouro, bairro, cidade, uf } = zipCodeAddressInformationData;

        setValue("endereco", logradouro || "", { shouldValidate: true, shouldDirty: true });
        setValue("bairro", bairro || "", { shouldValidate: true, shouldDirty: true });
        setValue("cidade", cidade || "", { shouldValidate: true, shouldDirty: true });
        setValue("uf", uf || "", { shouldValidate: true, shouldDirty: true });
      } else {
        setValue("endereco", "", { shouldValidate: true, shouldDirty: true });
        setValue("bairro", "", { shouldValidate: true, shouldDirty: true });
        setValue("cidade", "", { shouldValidate: true, shouldDirty: true });
        setValue("uf", "", { shouldValidate: true, shouldDirty: true });
      }

      setValue("complemento", "", { shouldValidate: true, shouldDirty: true });
      setValue("numeroEndereco", "", { shouldValidate: true, shouldDirty: true });
    }
  }, [zipCodeAddressInformationStatus]);

  const handleForm = (data) => {
    const payload = {
      email: data.email,
      pessoa_responsavel: data.pessoaResponsavel,
      contato: data.contato,
      cep: data.cep,
      endereco: data.endereco,
      numero_endereco: data.numeroEndereco,
      complemento: data?.complemento || undefined,
      bairro: data.bairro,
      uf: data.uf,
      cidade: data.cidade,
      latitude: parseFloat(data.latitude.replace(",", ".")),
      longitude: parseFloat(data.longitude.replace(",", ".")),
      polo: selectedHubInfos.children,
      id_polo: getProviderInformationData?.id_polo,
    };
    console.log(payload);
    dispatch(postProviderInformation(payload));
  };

  const emailsOptions = (employeesInfosLogisticData) =>
    employeesInfosLogisticData?.map((item) => ({
      value: item.email,
      label: item.email,
    }));


  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 2) return `${numbers}`;
    if (numbers.length <= 7)
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const formatCep = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const contatoRaw = watch("contato") || "";
  const cepRaw = watch("cep") || "";

  const formattedContato = formatPhone(contatoRaw);
  const formattedCep = formatCep(cepRaw);

  useEffect(() => {
    reset();
    if (postProviderInformationStatus === "fulfilled") {
      dispatch(setResetPostProviderInformationStatus())
    }
  }, [postProviderInformationStatus]);

  return (
    <>
      {() => handleShowNotification("morango")}
      <Dialog.Root
        open={openCloseModal}
        onCancel={() => setOpenCloseModal(false)}
      >
        <Dialog.Header title="Atenção" />

        <Dialog.ContentHeader
          ilustration={<img src={warning} />}
          description="Ao cancelar, todas as informações inseridas irão ser perdidas. Tem certeza de que deseja continuar?"
        />

        <Dialog.Footer>
          <Button onClick={() => setOpenCloseModal(false)}>Voltar</Button>
          <Button
            type="negative"
            onClick={() => {
              setOpenCloseModal(false);
              setOpenProviderEditDrawer();
              reset();
              dispatch(clearZipCodeAddressInformation());
            }}
          >
            Confirmar cancelamento
          </Button>
        </Dialog.Footer>
      </Dialog.Root>

      <Drawer.Root
        open={openProviderEditDrawer}
        onClose={() => setOpenProviderEditDrawer(false)}
        getContainer={false}
        title={<Drawer.Header title="Editar polo" date={new Date()} />}
      >
        <Drawer.Body>
          <form onSubmit={handleSubmit(handleForm)} id="provider-form">
            <S.AllDrawerProviderEditing>
              <FormInput
                label="Razão social"
                size="small"
                disabled
                control={control}
                name="razaoSocial"
              />
              <FormInput
                label="Nome fantasia"
                size="small"
                disabled
                control={control}
                name="nomeFantasia"
              />
              <FormInput
                label="CPF/CNPJ"
                size="small"
                disabled
                control={control}
                name="cpf"
              />
              <FormSelect
                label="E-mail"
                size="small"
                placeholder="Digite ou selecione um e-mail"
                allowClear
                control={control}
                name="email"
                showSearch
                className="ginga-team-select"
                options={emailsOptions(employeesInfosLogisticData)}
                onChange={(value) => {
                  if (!value) {
                    setValue("email", null);
                    setValue("pessoaResponsavel", null, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  } else {
                    setValue("email", value);

                    if (value.includes("@")) {
                      const namePart = value.split("@")[0];
                      const nameFormatted = namePart
                        .split(".")
                        .map(toCapitalizeFirstLetterOfEachWord)
                        .join(" ");

                      setValue("pessoaResponsavel", nameFormatted, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }
                  }
                }}
              />
              <FormInput
                label="Pessoa responsável"
                size="small"
                placeholder="Nome e sobrenome"
                allowClear
                control={control}
                name={"pessoaResponsavel"}
              />
              <FormInput
                label="Contato"
                size="small"
                placeholder="(DDD) 000000000"
                allowClear
                control={control}
                name="contato"
                value={formattedContato}
              />
              <FormInput
                label="CEP"
                size="small"
                placeholder="00000-000"
                allowClear
                control={control}
                name="cep"
                onChange={(e) => onChangeZipCode(e)}
                loading={zipCodeAddressInformationStatus === "pending"}
                value={formattedCep}
              />
              <p style={{ marginTop: "-7.9232px" }}>
                Após o preenchimento do CEP, as informações do endereço serão preenchidas automaticamente.
              </p>
              <FormInput
                label="Endereço"
                size="small"
                placeholder="Digite aqui"
                control={control}
                name="endereco"
                allowClear={zipCodeAddressInformationStatus !== "pending"}
                loading={zipCodeAddressInformationStatus === "pending"}
              />
              <S.DrawerGridInputs>
                <FormInput
                  label="Número"
                  size="small"
                  placeholder="0000"
                  allowClear
                  control={control}
                  name="numeroEndereco"
                />
                <FormInput
                  label="Complemento"
                  size="small"
                  placeholder="Opcional"
                  allowClear
                  control={control}
                  name="complemento"
                />
              </S.DrawerGridInputs>
              <FormInput
                label="Bairro"
                size="small"
                placeholder="Digite aqui"
                control={control}
                name="bairro"
                allowClear={zipCodeAddressInformationStatus !== "pending"}
                loading={zipCodeAddressInformationStatus === "pending"}
              />
              <S.DrawerGridInputsSecond>
                <FormInput
                  label="Cidade"
                  size="small"
                  placeholder="Digite aqui"
                  control={control}
                  name="cidade"
                  allowClear={zipCodeAddressInformationStatus !== "pending"}
                  loading={zipCodeAddressInformationStatus === "pending"}
                />
                <FormSelect
                  label="Estado"
                  size="small"
                  placeholder="UF"
                  options={brazillianStates}
                  control={control}
                  name="uf"
                  loading={zipCodeAddressInformationStatus === "pending"}
                />
              </S.DrawerGridInputsSecond>

              <S.DrawerGridInputsLoc>
                <FormInput
                  label="Latitude"
                  size="small"
                  allowClear
                  control={control}
                  name="latitude"
                />
                <FormInput
                  label="Longitude"
                  size="small"
                  allowClear
                  control={control}
                  name="longitude"
                />
              </S.DrawerGridInputsLoc>

            </S.AllDrawerProviderEditing>
          </form>
        </Drawer.Body>
        <Drawer.Footer>
          <Button
            width="100%"
            type="primary"
            size="medium"
            htmlType="submit"
            form="provider-form"
            disabled={!formState.isValid}
            loading={postProviderInformationStatus === "pending"}
          >
            Salvar alterações
          </Button>
          <Button
            width="100%"
            size="medium"
            onClick={() => {
              setOpenCloseModal(true);
            }}
          >
            Cancelar
          </Button>
        </Drawer.Footer>
      </Drawer.Root>
    </>
  );
};

export default InfosProviderEditing;
